/* Language Surface Cache — IndexedDB + localStorage fallback */
/* (C) 2024 - 2025, by Drew Anderson  - Cloud Media Works, LLC - All Rights Reserved */

// v3: source hash now covers ONLY the translatable surface (presets/labels/footer),
// not the whole bot + settings objects — see renderSurface in IndexPage/IndexPageGuest.
export const LANGUAGE_SURFACE_SCHEMA_VERSION = '3'

const DB_NAME = 'ibot-language-surfaces'
const STORE_NAME = 'rendered-surfaces'

export type RenderedLanguageSurfaceCache = {
  botId: string
  language: string
  sourceHash: string
  surface: unknown
  createdAt: string
  updatedAt: string
}

/* ── Cache key ─────────────────────────────────────────────────────────── */

export function buildSurfaceCacheKey(params: {
  botId: string
  language: string
  sourceHash: string
}): string {
  return [
    'surface',
    LANGUAGE_SURFACE_SCHEMA_VERSION,
    params.botId,
    params.language,
    params.sourceHash,
  ].join(':')
}

/* ── Source hash ───────────────────────────────────────────────────────── */

export async function buildSourceHashInput(params: {
  activeBot: unknown
  activeSettings: unknown
}): Promise<{ schemaVersion: string; activeBot: unknown; activeSettings: unknown }> {
  return {
    schemaVersion: LANGUAGE_SURFACE_SCHEMA_VERSION,
    activeBot: params.activeBot,
    activeSettings: params.activeSettings,
  }
}

export async function hashSurface(surface: unknown): Promise<string> {
  const json = JSON.stringify(surface)
  const encoded = new TextEncoder().encode(json)
  const digest = await crypto.subtle.digest('SHA-256', encoded)

  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 16)
}

/* ── IndexedDB core ────────────────────────────────────────────────────── */

function openLanguageSurfaceDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1)

    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME)
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

async function getRenderedSurface(cacheKey: string): Promise<unknown | null> {
  const db = await openLanguageSurfaceDb()

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly')
    const store = tx.objectStore(STORE_NAME)
    const req = store.get(cacheKey)

    req.onsuccess = () => {
      const record = req.result as RenderedLanguageSurfaceCache | undefined
      resolve(record?.surface ?? null)
    }

    req.onerror = () => reject(req.error)
  })
}

async function saveRenderedSurface(
  cacheKey: string,
  record: RenderedLanguageSurfaceCache,
): Promise<void> {
  const db = await openLanguageSurfaceDb()

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)
    const req = store.put(record, cacheKey)

    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })
}

/* ── Fallback wrappers ─────────────────────────────────────────────────── */

export async function getRenderedSurfaceWithFallback(
  cacheKey: string,
): Promise<unknown | null> {
  try {
    return await getRenderedSurface(cacheKey)
  } catch {
    const raw = localStorage.getItem(cacheKey)
    if (!raw) return null
    try {
      return (JSON.parse(raw) as RenderedLanguageSurfaceCache)?.surface ?? null
    } catch {
      return null
    }
  }
}

export async function saveRenderedSurfaceWithFallback(
  cacheKey: string,
  record: RenderedLanguageSurfaceCache,
): Promise<void> {
  try {
    await saveRenderedSurface(cacheKey, record)
    return
  } catch {
    try {
      localStorage.setItem(cacheKey, JSON.stringify(record))
    } catch {
      // Browser storage unavailable or quota exceeded — continue without persistence.
    }
  }
}

/* ── Surface validation ────────────────────────────────────────────────── */

/**
 * Lenient per-key merge of englishSurface and renderedSurface.
 * - Iterates keys from the English surface (the source of truth for structure).
 * - For each key, uses the translated value when it is the same type as the English.
 * - Falls back to the English value if the translation is missing, null, or a type mismatch.
 * - Arrays must have the same length; objects recurse.
 * - If renderedSurface is missing altogether, returns englishSurface unchanged.
 */
export function validateRenderedSurface(
  englishSurface: unknown,
  renderedSurface: unknown,
): unknown {
  // Not an object — cannot be a valid surface
  if (!renderedSurface || typeof renderedSurface !== 'object' || Array.isArray(renderedSurface)) {
    return englishSurface
  }
  if (!englishSurface || typeof englishSurface !== 'object' || Array.isArray(englishSurface)) {
    return renderedSurface
  }

  const eng = englishSurface as Record<string, unknown>
  const ren = renderedSurface as Record<string, unknown>
  const result: Record<string, unknown> = {}

  for (const key of Object.keys(eng)) {
    const engVal = eng[key]
    const renVal = ren[key]

    // Translation dropped this field — keep English
    if (renVal === undefined || renVal === null) {
      result[key] = engVal
      continue
    }

    // Same-length arrays — accept translation as-is (element types already verified by API prompt)
    if (Array.isArray(engVal) && Array.isArray(renVal)) {
      result[key] = renVal.length === engVal.length ? renVal : engVal
      continue
    }

    // Nested plain objects — recurse
    if (
      engVal !== null && typeof engVal === 'object' && !Array.isArray(engVal) &&
      renVal !== null && typeof renVal === 'object' && !Array.isArray(renVal)
    ) {
      result[key] = validateRenderedSurface(engVal, renVal)
      continue
    }

    // Primitives — accept if same typeof (string→string, number→number, boolean→boolean)
    if (typeof renVal === typeof engVal) {
      result[key] = renVal
      continue
    }

    // Type mismatch — keep English
    result[key] = engVal
  }

  return result
}

/* ── Translation endpoint caller ───────────────────────────────────────── */

export async function renderSurfaceLanguage(params: {
  surface: unknown
  language: string
  botId?: string
}): Promise<unknown> {
  try {
    // Same-origin POST. No Authorization header: the server uses the bot-specific OpenAI
    // key resolved from system.json (keyed by botId). Must work for guests — no authToken.
    const response = await fetch('/api/render-language-surface', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    })

    if (!response.ok) {
      return params.surface
    }

    const data = await response.json() as { surface?: unknown }
    return data.surface ?? params.surface
  } catch {
    return params.surface
  }
}
